import React from 'react';
import { createRoot } from 'react-dom/client';
import { toJS } from 'mobx';
import { Provider } from 'mobx-react';
import { BrowserRouter as Router } from 'react-router-dom';
import { init } from '@sentry/browser';
// import { FlagProvider } from '@unleash/proxy-client-react';

import rootStore from './store/RootStore';
import './i18n/i18n';
import { IsMobileProvider } from './hooks/useIsMobile';
import { ThemeProvider, GlobalStyles } from './theme';
import App from './App';

import * as serviceWorker from './serviceWorker';
import 'antd/dist/reset.css';
import 'cookieconsent/build/cookieconsent.min.css';
import { initializeGoogleAnalytics } from './utils/analytics';
import { initializeCookieConsent } from './utils/cookieConsent';

init({
  dsn: import.meta.env.REACT_APP_SENTRY_DSN,
  environment: import.meta.env.REACT_APP_ENV,
  release: import.meta.env.REACT_APP_VERSION,
});

window.toJS = toJS;
window.rootStore = rootStore;

// This must run before GTM script loads to set up dataLayer properly
initializeGoogleAnalytics();

// Initialize cookie consent when DOM is ready
// Dynamically import cookieconsent to ensure it loads before initialization
const initCookieConsent = async () => {
  await import('cookieconsent/build/cookieconsent.min.js');
  initializeCookieConsent();
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initCookieConsent, { once: true });
} else {
  initCookieConsent();
}

const container = document.getElementById('root');
const root = createRoot(container);

// TO DO: UNLEASH
// const unleashConfig = {
//   url: import.meta.env.VITE_UNLEASH_URL || '',
//   clientKey: import.meta.env.VITE_UNLEASH_CLIENT_KEY || '',
//   appName: 'unleash-onboarding-react',
//   environment: import.meta.env.VITE_UNLEASH_ENVIRONMENT || '',
// };

root.render(
  <ThemeProvider>
    <GlobalStyles theme={undefined} />
    {/* <FlagProvider config={unleashConfig}> // TO DO: UNLEASH */}
    <Provider
      userStore={rootStore.userStore}
      stationStore={rootStore.stationStore}
      alertStore={rootStore.alertStore}
      trendsStore={rootStore.trendsStore}
      rulesStore={rootStore.rulesStore}
      proactiveEngineStore={rootStore.proactiveEngineStore}
      permissionStore={rootStore.permissionStore}
      devicesStore={rootStore.devicesStore}
      investigationStore={rootStore.investigationStore}
      controlCenterStore={rootStore.controlCenterStore}
      usersStore={rootStore.usersStore}
      associableEntitiesStore={rootStore.associableEntitiesStore}
      mapBuilderStore={rootStore.mapBuilderStore}
      energySavingsStore={rootStore.energySavingsStore}
      energySavingsGraphStore={rootStore.energySavingsGraphStore}
      eulaStore={rootStore.eulaStore}
      stationRiskStore={rootStore.stationRiskStore}
      stationRiskGraphStore={rootStore.stationRiskGraphStore}
      leakageStore={rootStore.leakageStore}
      insightsStore={rootStore.insightsStore}
      engineStore={rootStore.engineStore}
      installationValidationStore={rootStore.installationValidationStore}
      siteSurveyStore={rootStore.siteSurveyStore}
      imageUploadStore={rootStore.imageUploadStore}
      lifecycleStore={rootStore.lifecycleStore}
      pidEditStore={rootStore.pidEditStore}
      reportStore={rootStore.reportStore}
      stationReadinessScoreStore={rootStore.stationReadinessScoreStore}
      chatbotStore={rootStore.chatbotStore}
    >
      <Router>
        <IsMobileProvider>
          <App />
        </IsMobileProvider>
      </Router>
    </Provider>
    {/* </FlagProvider> */}
  </ThemeProvider>,
);

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://bit.ly/CRA-PWA
serviceWorker.unregister();
